import * as React from 'react';
import {ContainerQuery} from 'react-container-query';
import {connect} from 'react-redux';

import _ from 'underscore';

import {Session} from 'nutshell-core/session';
import type {User} from 'nutshell-core/types';

import {EmptyAddCard} from 'shells/card/empty-add-card';

import type {Flow} from 'flow-to-typescript-codemod';

import {PageTitleBar} from '../../layout';

// @ts-expect-error
import * as actions from '../dashboard-actions';
import {getDashComponent} from '../dashboard-component-map';
import {
    getDashIsConfigurable,
    getIsDashSpecialCard,
    getDashObfuscationMessage,
    getDashIsGated,
    ERROR_TYPE_FETCHING,
    ERROR_TYPE_DELETING,
} from '../dashboard-constants';
// @ts-expect-error
import {getDashSubheader} from '../dashboard-helpers';
// @ts-expect-error
import * as selectors from '../dashboard-selectors';
import type {DashType} from '../types';

import {DashboardCTACards} from './cards/dashboard-cta-cards';
import {Dash} from './dash';
// @ts-expect-error
import {DashGridContainer} from './dash-grid-container';
// @ts-expect-error
import {DashLoading} from './dash-loading';
import {DashSlot} from './dash-slot';
import {Iframe} from './dashes/iframe/iframe';

import styles from './dash-grid.module.css';

const query = {
    'card-wide': {
        maxWidth: 740,
    },
    'width-less-than-900': {
        maxWidth: 900,
    },
    'width-less-than-500': {
        maxWidth: 500,
    },
} as const;
type QueryResults = Flow.ObjMap<typeof query, MakeBoolean>;
type MakeBoolean = (o: any) => boolean;

type OwnProps = {
    preventScrolling: boolean;
};

type StateProps = {
    dashes: {
        id: string;
        type: DashType;
        name: string;
        error: {
            msg: string;
            type: string;
        };
        errorWasDismissed: boolean;
        // This data prop has a bunch of different values shoved into it, and
        // differs depending on the dashboard type. We're only using these
        // values here, though.
        data: {
            dashId: string;
            stagesetName: string | null | undefined;
        };
        value: string;
        isRequestingComponentData: boolean;
    }[];
    dashIdBeingConfirmedToDelete?: string;
    dashIdBeingDeleted?: string;
    loggedInUser: User | null | undefined;
};

// @ts-expect-error
const mapStateToProps = function (state): StateProps {
    const dashes = selectors.getDashes(state);

    return {
        dashes: dashes,
        dashIdBeingConfirmedToDelete: selectors.getDashIdBeingConfirmedToDelete(state),
        dashIdBeingDeleted: selectors.getDashIdBeingDeleted(state),
        loggedInUser: Session.getSessionStateUser(state),
    };
};

type DispatchProps = {
    handleAddDash: () => void;
    handleDashDrop: (arg1: {id: string; index: number}) => void;
    handleDashHover: (arg1: {id: string; index: number}) => void;
    handleConfigDashPress: (arg1: string) => void;
    handleRemoveDashPress: (arg1: string) => void;
    onDeleteDashConfirm: (arg1: string) => void;
    fetchDashData: (arg1?: string | null | undefined) => void;
    handleDismissError: (arg1: string) => void;
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        handleAddDash: () => {
            dispatch(actions.addNewDash());
        },
        handleDashDrop: (payload) => {
            dispatch(actions.dashWasDropped(payload));
        },
        handleDashHover: (payload) => {
            dispatch(actions.hoverDash(payload));
        },
        handleConfigDashPress: (id) => {
            dispatch(actions.toggleConfigureDash(id));
        },
        handleRemoveDashPress: (id) => {
            dispatch(actions.toggleDeleteDash(id));
        },
        onDeleteDashConfirm: (id) => {
            actions.deleteDash(dispatch, id);
        },
        fetchDashData: (id) => {
            dispatch(actions.fetchDashboardComponentData(id));
        },
        handleDismissError: (id) => {
            dispatch(actions.dismissDashComponentFailureById(id));
        },
    };
};

type ContainerQueryProps = {
    containerQuery: QueryResults;
};

type Props = OwnProps & StateProps & DispatchProps & ContainerQueryProps;

class DashGridComponent extends React.Component<Props> {
    render() {
        const dashes = this.props.dashes
            // Do not show old dashboard onboarding card
            .filter((dash) => dash.id !== 'onboarding00-dashes')
            .map((dash, index) => {
                const error = dash.error;
                const isDeleteConfirming = dash.id === this.props.dashIdBeingConfirmedToDelete;
                const isRefreshingComponent =
                    dash.isRequestingComponentData && !_.isEmpty(dash.data);
                if (this.props.dashIdBeingDeleted !== dash.id) {
                    // @ts-expect-error
                    if (dash.type === 'iframe') {
                        return (
                            <DashSlot
                                key={dash.id}
                                index={index}
                                onDashHover={this.props.handleDashHover}
                                isDropTarget={true}
                                containerQuery={this.props.containerQuery}
                            >
                                <Dash
                                    id={dash.id}
                                    index={index}
                                    title={dash.name}
                                    subheader={'External website'}
                                    data={{}}
                                    onDismissError={() => {}}
                                    onErrorRetry={() => {}}
                                    isConfigurable={false}
                                    onDashDrop={this.props.handleDashDrop}
                                >
                                    <Iframe url={dash.value} />
                                </Dash>
                            </DashSlot>
                        );
                    } else if (getIsDashSpecialCard(dash.type)) {
                        const Card = getDashComponent(dash.id);

                        return Card ? (
                            <DashSlot key={dash.id} containerQuery={this.props.containerQuery}>
                                <Card
                                    loggedInUser={this.props.loggedInUser}
                                    onClose={() => this.handleRemoveSpecialCard(dash.id)}
                                />
                            </DashSlot>
                        ) : null;
                    } else {
                        return (
                            <DashSlot
                                key={dash.id}
                                index={index}
                                onDashHover={this.props.handleDashHover}
                                isDropTarget={true}
                                containerQuery={this.props.containerQuery}
                            >
                                <Dash
                                    id={dash.id}
                                    isRefreshingComponent={isRefreshingComponent}
                                    isDeleteConfirming={isDeleteConfirming}
                                    index={index}
                                    title={dash.name}
                                    subheader={getDashSubheader(dash)}
                                    data={dash.data}
                                    error={error}
                                    errorWasDismissed={dash.errorWasDismissed}
                                    onDismissError={this.props.handleDismissError}
                                    obfuscationMessage={
                                        getDashIsGated(dash.type)
                                            ? getDashObfuscationMessage(dash.type)
                                            : undefined
                                    }
                                    isConfigurable={getDashIsConfigurable(dash.type)}
                                    onRemove={this.props.handleRemoveDashPress}
                                    onDeleteDashConfirm={this.props.onDeleteDashConfirm}
                                    onDashDrop={this.props.handleDashDrop}
                                    onConfigure={this.props.handleConfigDashPress}
                                    // @ts-expect-error
                                    onErrorRetry={(dashId) => this.handleErrorRetry(error, dashId)}
                                >
                                    {this.renderDashComponent(dash, this.props.loggedInUser)}
                                </Dash>
                            </DashSlot>
                        );
                    }
                } else {
                    return null;
                }
            });

        return (
            <DashGridContainer>
                <PageTitleBar
                    title='Dashboard'
                    subheaderPrimaryText='Add cards to track the metrics, leads and reports that matter most to you'
                />
                <DashboardCTACards
                    isNarrow={this.props.containerQuery['width-less-than-900']}
                    isTiny={this.props.containerQuery['width-less-than-500']}
                />
                <div
                    className={
                        this.props.preventScrolling
                            ? styles['dash-grid--no-scroll']
                            : styles['dash-grid']
                    }
                >
                    {dashes}
                    <DashSlot containerQuery={this.props.containerQuery}>
                        <div id='add-dashboard-card' className='full-height'>
                            <EmptyAddCard
                                background='white'
                                size='large'
                                text='Add a card'
                                onClick={this.props.handleAddDash}
                            />
                        </div>
                    </DashSlot>
                </div>
            </DashGridContainer>
        );
    }

    // @ts-expect-error
    renderDashComponent = (dash, loggedInUser) => {
        const isLoadingComponent = dash.isRequestingComponentData;
        const DashComponent = getDashComponent(dash.type);
        if (isLoadingComponent && _.isEmpty(dash.data)) {
            return <DashLoading />;
        }

        return DashComponent ? (
            <DashComponent {...dash.data} query={dash.value} loggedInUser={loggedInUser} />
        ) : null;
    };

    // @ts-expect-error
    handleErrorRetry = (error, dashId) => {
        if (error.type === ERROR_TYPE_FETCHING) {
            this.props.fetchDashData();
        } else if (error.type === ERROR_TYPE_DELETING) {
            this.props.onDeleteDashConfirm(dashId);
        }
    };

    // @ts-expect-error
    handleRemoveSpecialCard = (id) => {
        this.props.onDeleteDashConfirm(id);
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps & ContainerQueryProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps
);
const DashGridConnected = connector(DashGridComponent);

export const DashGrid = (props: OwnProps) => {
    // Scroll to the dashboard add button when navigated to the page from the
    // onboarding module
    React.useEffect(() => {
        if (window.location.hash === '#add-dashboard-card') {
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            let attempts = 0;
            const maxAttempts = 10;
            const retryDelayMs = 300;

            const scrollToCard = () => {
                const element = document.getElementById('add-dashboard-card');
                if (element) {
                    element.scrollIntoView({behavior: 'smooth', block: 'center'});
                } else if (attempts < maxAttempts) {
                    attempts += 1;
                    timeoutId = setTimeout(scrollToCard, retryDelayMs);
                }
            };

            // Initial delay to let the dashboard content mount and settle
            timeoutId = setTimeout(scrollToCard, 500);

            return () => {
                if (timeoutId !== null) {
                    clearTimeout(timeoutId);
                }
            };
        }
    }, []);

    return (
        <ContainerQuery query={query}>
            {/* @ts-expect-error */}
            {(containerParams: QueryResults) => (
                <DashGridConnected {...props} containerQuery={containerParams} />
            )}
        </ContainerQuery>
    );
};
